import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BadgePercent,
  Home,
  Database,
  Wrench,
  BookOpen,
  Users,
  User,
  UserCircle,
  GraduationCap,
  CreditCard,
  Settings,
  LogOut,
  Mail,
  X,
  PanelLeft,
  PanelRight,
  Wallet,
  PiggyBank,
  Package,
  UserCheck,
  ShoppingCart,
  Contact,
  Flame,
  Building2,
  HelpCircle,
  ShieldCheck,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import apiClient from '@/services/api/client';
import { useUnmatchedEmailCount } from '@/hooks/useUnmatchedEmails';
import { useOrgMode } from '@/hooks/useOrgMode';
import type { OrganizationMode } from '@/types/settings';
import { SidebarGroup, type SidebarLink } from './SidebarGroup';
import { useAccounts } from '@/hooks/useAccounts';
import { isManagerUser, isPathAllowedForManager } from '@/lib/managerAccess';

interface CurrentUser {
  role?: string;
  /** KAN-988: функции тарифа кабинета. */
  features?: string[];
  /** KAN-980: суперпользователь платформы. */
  is_superuser?: boolean;
}

interface MenuGroup {
  group: string;
  icon: LucideIcon;
  label: string;
  items: SidebarLink[];
}

type MenuEntry = SidebarLink | MenuGroup;

const isGroup = (entry: MenuEntry): entry is MenuGroup => 'group' in entry;

interface Viewer {
  isOwner: boolean;
  isManager: boolean;
  mode: OrganizationMode | undefined;
  features: string[];
  isSuperuser: boolean;
}

/**
 * Пункт виден: не только для владельца (или это владелец), менеджеру только
 * разделы из allow-list путей isPathAllowedForManager (KAN-811, то же правило,
 * что у ManagerRouteGuard), подходит режиму кабинета и функции тарифа (KAN-988).
 */
const isLinkVisible = (link: SidebarLink, { isOwner, isManager, mode, features, isSuperuser }: Viewer): boolean =>
  (!link.superuserOnly || isSuperuser) &&
  (!link.ownerOnly || isOwner) &&
  (!isManager || isPathAllowedForManager(link.path)) &&
  (!link.modes || (mode !== undefined && link.modes.includes(mode))) &&
  (!link.feature || features.includes(link.feature));

const menuItems: MenuEntry[] = [
  { icon: Home, label: 'Дашборд', path: '/' },
  {
    group: 'agent-training',
    icon: GraduationCap,
    label: 'Обучение агентов',
    items: [
      { icon: Database, label: 'Источники данных', path: '/data-sources', ownerOnly: true },
      // KAN-987: услуги и итоговый документ только у режима "Услуги".
      { icon: Wrench, label: 'Услуги', path: '/services', modes: ['estimator'] },
      { icon: BookOpen, label: 'Итоговый документ', path: '/merged-document', modes: ['estimator'] },
    ],
  },
  // KAN-746: заказы интернет-магазина. KAN-987: пункт смет убран во всех режимах.
  { icon: ShoppingCart, label: 'Заказы', path: '/orders', modes: ['ecommerce'] },
  // KAN-812: товары интернет-магазина одной группой.
  {
    group: 'products',
    icon: Package,
    label: 'Товары',
    items: [
      // KAN-743: товары из фида, только у интернет-магазина.
      { icon: Package, label: 'Каталог', path: '/catalog', modes: ['ecommerce'] },
      // KAN-755: скидки на товары и группы товаров, только у интернет-магазина.
      { icon: BadgePercent, label: 'Скидки', path: '/discounts', modes: ['ecommerce'] },
    ],
  },
  // KAN-812: работа с клиентами одной группой.
  {
    group: 'clients',
    icon: UserCircle,
    label: 'Клиенты',
    items: [
      // KAN-756: доска CRM (внимание и клиенты по дням), только у интернет-магазина.
      // KAN-988: и только при функции тарифа crm.
      { icon: Contact, label: 'CRM', path: '/crm', modes: ['ecommerce'], feature: 'crm' },
      // KAN-810: открытые лиды по теплоте, только у интернет-магазина.
      { icon: Flame, label: 'Лиды по теплоте', path: '/leads', modes: ['ecommerce'], feature: 'crm' },
      // "Обзвон" merged into "Контакты" as the cold-calls tab (TASK-337, AC-3);
      // /clients/cold-calls* keeps "Контакты" highlighted via startsWith matching.
      { icon: UserCircle, label: 'Контакты', path: '/clients' },
      // KAN-826: организации клиентов (реквизиты и контакты), в любом режиме.
      { icon: Building2, label: 'Организации', path: '/companies' },
    ],
  },
  // KAN-718: журнал писем клиентам и ответов от них.
  { icon: Mail, label: 'Почтовый ящик', path: '/emails' },
  // Телефония и Telegram-аккаунты: вкладки Настроек (/settings?tab=...).
  // KAN-812: пользователи кабинета и менеджеры одной группой.
  {
    group: 'team',
    icon: Users,
    label: 'Команда',
    items: [
      { icon: Users, label: 'Пользователи', path: '/users', ownerOnly: true },
      // KAN-744: менеджеры и их конверсия; правит только владелец, видят все.
      { icon: UserCheck, label: 'Менеджеры', path: '/managers' },
    ],
  },
  // KAN-812: деньги кабинета одной группой, только владельцу.
  {
    group: 'finance',
    icon: Wallet,
    label: 'Финансы',
    items: [
      // KAN-341: тариф, пакет минут и платежи; суммы и история платежей не для всех.
      { icon: CreditCard, label: 'Биллинг', path: '/billing', ownerOnly: true },
      // KAN-976: кошелёк кабинета: остаток, пополнение, журнал списаний.
      { icon: PiggyBank, label: 'Баланс', path: '/balance', ownerOnly: true },
      // KAN-735: расходы на прокси, аккаунты и токены.
      { icon: Wallet, label: 'Расходы', path: '/expenses', ownerOnly: true },
    ],
  },
  { icon: Settings, label: 'Настройки', path: '/settings' },
  // KAN-951: видео и текст по разделам кабинета, всем ролям, последним пунктом.
  { icon: HelpCircle, label: 'Помощь', path: '/help' },
  // KAN-980 / KAN-979: раздел суперпользователя: кабинеты, кошельки, пул Telegram.
  { icon: ShieldCheck, label: 'Платформа', path: '/platform', superuserOnly: true },
];

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

// nav px-2 (8px) + item px-3.5 (14px) = 22px to icon left edge.
// Icon center = 22 + 10 = 32px = exact center of collapsed sidebar (64px / 2).
// Same position in expanded sidebar — icons never jump.
const NAV_PAD = 'px-2';
const ITEM_PAD = 'px-3.5';

export function Sidebar({ isOpen = false, onClose, isCollapsed = false, onToggleCollapse }: SidebarProps) {
  const location = useLocation();

  const { data: currentUser } = useQuery<CurrentUser>({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/auth/me/');
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const orgMode = useOrgMode();
  const viewer: Viewer = {
    isOwner: currentUser?.role === 'OWNER',
    isManager: isManagerUser(currentUser),
    mode: orgMode,
    features: currentUser?.features ?? [],
    isSuperuser: currentUser?.is_superuser === true,
  };
  // Группа без видимых подпунктов не рисуется (KAN-811: у менеджера "Обучение агентов" пустая).
  const visibleMenu = menuItems
    .map(item => (isGroup(item) ? { ...item, items: item.items.filter(child => isLinkVisible(child, viewer)) } : item))
    .filter(item => (isGroup(item) ? item.items.length > 0 : isLinkVisible(item, viewer)));
  // KAN-724: красный счётчик у пунктов меню (по пути пункта): письма без привязки у почтового ящика.
  const unmatchedEmails = useUnmatchedEmailCount();
  const badges: Record<string, number> = { '/emails': unmatchedEmails };

  // KAN-925: выход из текущего аккаунта; остальные вошедшие остаются (переход на следующий или на вход).
  const { logout: handleLogout } = useAccounts();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleNavClick = () => {
    onClose?.();
  };

  const isPathActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 md:hidden transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className={[
          'bg-white border-r border-gray-200 flex flex-col overflow-hidden',
          'w-64',
          isCollapsed ? 'md:w-16' : 'md:w-64',
          'fixed top-0 left-0 h-full z-50 shadow-xl',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          'md:relative md:translate-x-0 md:z-auto md:shadow-none md:h-auto',
          'transition-[width,transform] duration-300 ease-in-out',
        ].join(' ')}>

        {/* Logo header */}
        <div className={`flex items-center border-b border-gray-100 h-16 px-4 ${isCollapsed && !isOpen ? 'justify-center' : 'justify-between'}`}>
          <div className="flex items-center gap-2 min-w-0">
            {isCollapsed && !isOpen ? (
              <img src="/smeta_logo.svg" alt="Smeta AI" className="h-8 w-8 shrink-0 object-contain" />
            ) : (
              <h1 className="text-xl font-bold text-blue-600 whitespace-nowrap">SMETA AI</h1>
            )}
          </div>
          {(!isCollapsed || isOpen) && (
            <button className="md:hidden p-2 rounded-lg hover:bg-gray-100" onClick={onClose} aria-label="Закрыть меню">
              <X className="w-5 h-5 text-gray-600" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto">
          <nav className={`${NAV_PAD} py-4 space-y-1`}>
            {visibleMenu.map(item => {
              if (isGroup(item)) {
                return (
                  <SidebarGroup
                    key={item.group}
                    id={item.group}
                    icon={item.icon}
                    label={item.label}
                    items={item.items}
                    isActive={isPathActive}
                    compact={isCollapsed && !isOpen}
                    itemPad={ITEM_PAD}
                    onNavigate={handleNavClick}
                  />
                );
              }
              const Icon = item.icon;
              const isActive = isPathActive(item.path);
              const badge = badges[item.path] ?? 0;
              const compact = isCollapsed && !isOpen;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={handleNavClick}
                  title={(isCollapsed && !isOpen) ? item.label : undefined}
                  className={`relative flex items-center gap-3 h-11 ${ITEM_PAD} rounded-lg transition-colors overflow-hidden ${
                    isActive ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                  }`}>
                  <Icon className="w-5 h-5 shrink-0" />
                  {!compact && <span className="truncate">{item.label}</span>}
                  {badge > 0 && (
                    <span
                      data-testid={`sidebar-badge-${item.path}`}
                      aria-label={`Новых: ${badge}`}
                      className={`rounded-full bg-red-600 px-1.5 text-xs font-semibold leading-5 text-white ${
                        compact ? 'absolute right-1 top-1 min-w-[1.25rem] text-center' : 'ml-auto'
                      }`}>
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Toggle collapse — desktop only */}
        <div className={`${NAV_PAD} hidden md:block`}>
          <button
            onClick={onToggleCollapse}
            title={isCollapsed ? 'Развернуть меню' : undefined}
            aria-label={isCollapsed ? 'Развернуть меню' : 'Свернуть меню'}
            className={`flex items-center gap-3 h-11 ${ITEM_PAD} rounded-lg transition-colors text-gray-500 hover:bg-gray-100 hover:text-gray-700 w-full`}>
            {isCollapsed ? (
              <PanelRight className="w-5 h-5 shrink-0" />
            ) : (
              <>
                <PanelLeft className="w-5 h-5 shrink-0" />
                <span className="whitespace-nowrap">Свернуть</span>
              </>
            )}
          </button>
        </div>

        {/* Profile */}
        <div className={`${NAV_PAD}`}>
          <Link
            to="/profile"
            onClick={handleNavClick}
            title={(isCollapsed && !isOpen) ? 'Профиль' : undefined}
            className={`flex items-center gap-3 h-11 ${ITEM_PAD} rounded-lg transition-colors w-full ${
              location.pathname === '/profile'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <User className="w-5 h-5 shrink-0" />
            {(!isCollapsed || isOpen) && <span className="whitespace-nowrap">Профиль</span>}
          </Link>
        </div>

        {/* Divider */}
        <div className={`${NAV_PAD} my-2`} data-testid="sidebar-divider">
          <hr className="border-gray-200" />
        </div>

        {/* Logout */}
        <div className={`${NAV_PAD} pb-4`}>
          <button
            onClick={handleLogout}
            title={(isCollapsed && !isOpen) ? 'Выход' : undefined}
            className={`flex items-center gap-3 h-11 ${ITEM_PAD} rounded-lg transition-colors text-red-500 hover:bg-red-50 hover:text-red-600 w-full`}>
            <LogOut className="w-5 h-5 shrink-0" />
            {(!isCollapsed || isOpen) && <span className="whitespace-nowrap">Выход</span>}
          </button>
        </div>
      </aside>
    </>
  );
}
